import React, { useCallback, useEffect, useRef, useSyncExternalStore } from "react";

const scrollPositions = new Map<string, number>();
const scrollPositionListeners = new Map<string, Set<() => void>>();

const getPersistentScrollPosition = (scrollPositionKey: string | undefined) => {
  if (scrollPositionKey == null) return 0;

  return scrollPositions.get(scrollPositionKey) ?? 0;
};

const subscribeToPersistentScrollPosition = (
  scrollPositionKey: string | undefined,
  listener: () => void,
) => {
  if (scrollPositionKey == null) return () => {};

  let listeners = scrollPositionListeners.get(scrollPositionKey);
  if (listeners == null) {
    listeners = new Set();
    scrollPositionListeners.set(scrollPositionKey, listeners);
  }

  listeners.add(listener);

  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) {
      scrollPositionListeners.delete(scrollPositionKey);
    }
  };
};

const setPersistentScrollPosition = (scrollPositionKey: string | undefined, scrollTop: number) => {
  if (scrollPositionKey == null) return;

  const previousScrollTop = getPersistentScrollPosition(scrollPositionKey);
  if (previousScrollTop === scrollTop) return;

  scrollPositions.set(scrollPositionKey, scrollTop);
  scrollPositionListeners.get(scrollPositionKey)?.forEach((listener) => listener());
};

export const usePersistentScrollTop = (scrollPositionKey: string | undefined) => {
  const subscribe = useCallback(
    (listener: () => void) => subscribeToPersistentScrollPosition(scrollPositionKey, listener),
    [scrollPositionKey],
  );
  const getSnapshot = useCallback(
    () => getPersistentScrollPosition(scrollPositionKey),
    [scrollPositionKey],
  );

  return useSyncExternalStore(subscribe, getSnapshot, () => 0);
};

export const usePersistentScrollPosition = <TElement extends HTMLElement>(
  scrollPositionKey: string | undefined,
) => {
  const scrollRef = useRef<TElement | null>(null);

  useEffect(() => {
    if (scrollPositionKey == null) return;

    const node = scrollRef.current;
    const scrollTop = getPersistentScrollPosition(scrollPositionKey);
    if (node == null) return;

    node.scrollTop = scrollTop;
  }, [scrollPositionKey]);

  const rememberScrollPosition = useCallback(
    (event: React.UIEvent<TElement>) => {
      setPersistentScrollPosition(scrollPositionKey, event.currentTarget.scrollTop);
    },
    [scrollPositionKey],
  );

  return { rememberScrollPosition, scrollRef };
};
